import { ethers } from 'ethers';

export interface ContractValidationResult {
  contractName: string;
  address: string;
  isValid: boolean;
  isDeployed: boolean;
  abiCompatible: boolean;
  error?: string;
  debugInfo?: {
    bytecode?: string;
    abiVersion?: string;
    deploymentBlock?: number;
    networkId?: string;
    suggestions?: string[];
    validationError?: any;
  };
}

export interface NetworkEnvironmentInfo {
  isDevelopment: boolean;
  isProduction: boolean;
  networkType: 'mainnet' | 'testnet' | 'local' | 'unknown';
  chainId: string;
  networkName: string;
  rpcUrl?: string;
  blockExplorer?: string;
  debugMode: boolean;
}

export interface TransactionDebugInfo {
  transactionHash: string;
  blockNumber?: number;
  gasUsed?: string;
  gasLimit?: string;
  gasPrice?: string;
  status: 'pending' | 'success' | 'failed' | 'reverted';
  revertReason?: string;
  logs?: any[];
  debugTrace?: any;
  timestamp?: string;
}

export interface DevelopmentError extends Error {
  code: string;
  context: any;
  debugInfo?: any;
  suggestions?: string[];
}

/**
 * Contract deployment validator for development mode
 */
export class ContractDeploymentValidator {
  private provider: ethers.Provider;
  private developmentMode: boolean;

  constructor(provider: ethers.Provider) {
    this.provider = provider;
    this.developmentMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Validate contract deployment with comprehensive checks
   */
  async validateContractDeployment(
    contractName: string,
    address: string,
    expectedAbi?: any[]
  ): Promise<ContractValidationResult> {
    const result: ContractValidationResult = {
      contractName,
      address,
      isValid: false,
      isDeployed: false,
      abiCompatible: false,
      debugInfo: {}
    };

    try {
      // Check if address is valid
      if (!ethers.isAddress(address)) {
        result.error = 'Invalid contract address format';
        return result;
      }

      // Check if contract is deployed
      const bytecode = await this.provider.getCode(address);
      result.isDeployed = bytecode !== '0x';
      result.debugInfo!.bytecode = bytecode;

      if (!result.isDeployed) {
        result.error = 'Contract not deployed at this address';
        if (this.developmentMode) {
          result.debugInfo!.suggestions = [
            'Check if the contract is deployed to the correct network',
            'Verify the contract address in your configuration',
            'Run deployment scripts if using local network',
            'Check if the local blockchain is running'
          ];
        }
        return result;
      }

      // Get network information for debugging
      const network = await this.provider.getNetwork();
      result.debugInfo!.networkId = network.chainId.toString();

      // Try to get deployment block (for debugging)
      if (this.developmentMode) {
        try {
          const deploymentBlock = await this.findDeploymentBlock(address);
          result.debugInfo!.deploymentBlock = deploymentBlock;
        } catch (error) {
          console.warn('Could not find deployment block:', error);
        }
      }

      // Validate ABI compatibility if provided
      if (expectedAbi) {
        try {
          const contract = new ethers.Contract(address, expectedAbi, this.provider);
          
          // Test a few common functions to validate ABI
          const abiValidation = await this.validateAbiCompatibility(contract, contractName);
          result.abiCompatible = abiValidation.isCompatible;
          result.debugInfo!.abiVersion = abiValidation.version;
          
          if (!result.abiCompatible) {
            result.error = abiValidation.error;
          }
        } catch (abiError) {
          result.abiCompatible = false;
          result.error = `ABI validation failed: ${abiError instanceof Error ? abiError.message : 'Unknown error'}`;
        }
      } else {
        result.abiCompatible = true; // Assume compatible if no ABI provided
      }

      result.isValid = result.isDeployed && result.abiCompatible;

      return result;
    } catch (error) {
      result.error = `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      
      if (this.developmentMode) {
        result.debugInfo!.validationError = error;
        result.debugInfo!.suggestions = [
          'Check your internet connection',
          'Verify the RPC endpoint is working',
          'Ensure the contract address is correct',
          'Check if you\'re on the right network'
        ];
      }
      
      return result;
    }
  }

  /**
   * Find the deployment block of a contract (development mode only)
   */
  private async findDeploymentBlock(address: string): Promise<number | undefined> {
    if (!this.developmentMode) return undefined;

    try {
      const currentBlock = await this.provider.getBlockNumber();
      const searchRange = Math.min(1000, currentBlock); // Search last 1000 blocks or from genesis
      
      // Binary search for deployment block
      let low = Math.max(0, currentBlock - searchRange);
      let high = currentBlock;
      
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        try {
          const code = await this.provider.getCode(address, mid);
          if (code === '0x') {
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        } catch (e: any) {
          const msg = (e?.message || '').toLowerCase();
          const rpcCode = e?.code;
          const innerCode = e?.data?.code;
          if (
            rpcCode === -32603 ||
            innerCode === -32000 ||
            msg.includes('state histories') ||
            msg.includes('archive') ||
            msg.includes('not fully indexed')
          ) {
            // RPC does not support historical state lookups; skip deployment block detection
            return undefined;
          }
          throw e;
        }
      }
      
      return low <= currentBlock ? low : undefined;
    } catch (error) {
      // Silently skip if RPC cannot serve historical state
      return undefined;
    }
  }

  /**
   * Validate ABI compatibility with deployed contract
   */
  private async validateAbiCompatibility(
    contract: ethers.Contract,
    contractName: string
  ): Promise<{ isCompatible: boolean; version?: string; error?: string }> {
    try {
      // Define expected functions for different contract types
      const expectedFunctions: { [key: string]: string[] } = {
        'Pool': ['GetModel', 'Pre_request'],
        'WalletHub': ['deposit', 'userBalance'],
        'SWM': ['balanceOf', 'allowance', 'approve', 'permit'],
        'WETH': ['balanceOf', 'allowance', 'approve'],
        'HFSWM': ['balanceOf', 'allowance', 'approve'],
        'CRED': ['balanceOf', 'allowance', 'approve']
      };

      const functionsToCheck = expectedFunctions[contractName] || ['balanceOf'];
      
      // Check if expected functions exist
      for (const functionName of functionsToCheck) {
        if (!contract.interface.hasFunction(functionName)) {
          return {
            isCompatible: false,
            error: `Missing expected function: ${functionName}`
          };
        }
      }

      // Try to call a view function to test connectivity
      if (contractName === 'WalletHub') {
        // Test userBalance with zero address
        await contract.userBalance(ethers.ZeroAddress);
      } else if (functionsToCheck.includes('balanceOf')) {
        // Test balanceOf with zero address
        await contract.balanceOf(ethers.ZeroAddress);
      }

      return {
        isCompatible: true,
        version: 'compatible'
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Some errors are expected (like calling with zero address)
      if (errorMessage.includes('revert') || errorMessage.includes('invalid address')) {
        return {
          isCompatible: true,
          version: 'compatible'
        };
      }
      
      return {
        isCompatible: false,
        error: `ABI compatibility test failed: ${errorMessage}`
      };
    }
  }

  /**
   * Validate all contracts in the system
   */
  async validateAllContracts(contractConfigs: { [name: string]: { address: string; abi: any[] } }): Promise<ContractValidationResult[]> {
    const results: ContractValidationResult[] = [];
    
    for (const [name, config] of Object.entries(contractConfigs)) {
      const result = await this.validateContractDeployment(name, config.address, config.abi);
      results.push(result);
    }
    
    return results;
  }
}

/**
 * ABI compatibility checker for version mismatches
 */
export class AbiCompatibilityChecker {
  /**
   * Check ABI compatibility between expected and actual contract
   */
  static checkAbiCompatibility(
    expectedAbi: any[],
    contractAddress: string,
    provider: ethers.Provider
  ): Promise<{
    isCompatible: boolean;
    missingFunctions: string[];
    extraFunctions: string[];
    versionMismatch: boolean;
    suggestions: string[];
  }> {
    return new Promise(async (resolve) => {
      try {
        const contract = new ethers.Contract(contractAddress, expectedAbi, provider);
        const contractInterface = contract.interface;
        
        // Get all function names from ABI
        const expectedFunctions = expectedAbi
          .filter(item => item.type === 'function')
          .map(item => item.name);
        
        // Try to detect actual functions (this is limited without contract source)
        const missingFunctions: string[] = [];
        const workingFunctions: string[] = [];
        
        // Test each function to see if it exists
        for (const functionName of expectedFunctions) {
          try {
            if (contractInterface.hasFunction(functionName)) {
              workingFunctions.push(functionName);
            } else {
              missingFunctions.push(functionName);
            }
          } catch (error) {
            missingFunctions.push(functionName);
          }
        }
        
        const isCompatible = missingFunctions.length === 0;
        const versionMismatch = missingFunctions.length > 0 && workingFunctions.length > 0;
        
        const suggestions: string[] = [];
        if (!isCompatible) {
          suggestions.push('Update the ABI file to match the deployed contract');
          suggestions.push('Redeploy the contract with the expected ABI');
          suggestions.push('Check if you\'re using the correct contract address');
        }
        
        resolve({
          isCompatible,
          missingFunctions,
          extraFunctions: [], // Can't easily detect without contract source
          versionMismatch,
          suggestions
        });
      } catch (error) {
        resolve({
          isCompatible: false,
          missingFunctions: [],
          extraFunctions: [],
          versionMismatch: true,
          suggestions: [
            'Check if the contract is deployed correctly',
            'Verify the ABI file is valid JSON',
            'Ensure you\'re connected to the right network'
          ]
        });
      }
    });
  }
}

/**
 * Network environment detector and debugger
 */
export class NetworkEnvironmentDetector {
  private provider: ethers.Provider;

  constructor(provider: ethers.Provider) {
    this.provider = provider;
  }

  /**
   * Detect and analyze current network environment
   */
  async detectEnvironment(): Promise<NetworkEnvironmentInfo> {
    try {
      const network = await this.provider.getNetwork();
      const chainId = network.chainId.toString();
      
      const isDevelopment = process.env.NODE_ENV === 'development';
      const debugMode = isDevelopment || process.env.REACT_APP_DEBUG === 'true';
      
      // Determine network type
      let networkType: 'mainnet' | 'testnet' | 'local' | 'unknown' = 'unknown';
      let networkName = 'Unknown Network';
      let rpcUrl: string | undefined;
      let blockExplorer: string | undefined;
      
      // Common network detection
      switch (chainId) {
        case '1':
          networkType = 'mainnet';
          networkName = 'Ethereum Mainnet';
          blockExplorer = 'https://etherscan.io';
          break;
        case '5':
          networkType = 'testnet';
          networkName = 'Goerli Testnet';
          blockExplorer = 'https://goerli.etherscan.io';
          break;
        case '11155111':
          networkType = 'testnet';
          networkName = 'Sepolia Testnet';
          blockExplorer = 'https://sepolia.etherscan.io';
          break;
        case '1322837':
          networkType = 'testnet';
          networkName = 'Swarm Devnet';
          rpcUrl = 'https://rpc.devnet.swarmind.ai';
          blockExplorer = 'https://explorer.devnet.swarmind.ai';
          break;
        case '1337':
        case '31337':
          networkType = 'local';
          networkName = chainId === '1337' ? 'Hardhat Local' : 'Anvil Local';
          rpcUrl = 'http://127.0.0.1:8545';
          break;
        default:
          // Try to detect if it's a local network
          if (parseInt(chainId) > 1000000) {
            networkType = 'local';
            networkName = `Local Network (${chainId})`;
          }
      }
      
      return {
        isDevelopment,
        isProduction: !isDevelopment,
        networkType,
        chainId,
        networkName,
        rpcUrl,
        blockExplorer,
        debugMode
      };
    } catch (error) {
      console.error('Error detecting network environment:', error);
      
      return {
        isDevelopment: process.env.NODE_ENV === 'development',
        isProduction: process.env.NODE_ENV === 'production',
        networkType: 'unknown',
        chainId: '0',
        networkName: 'Unknown',
        debugMode: false
      };
    }
  }

  /**
   * Generate environment indicators for UI display
   */
  async generateEnvironmentIndicators(): Promise<{
    showIndicator: boolean;
    indicatorText: string;
    indicatorColor: 'green' | 'yellow' | 'red' | 'blue';
    warnings: string[];
  }> {
    const env = await this.detectEnvironment();
    
    const warnings: string[] = [];
    let indicatorColor: 'green' | 'yellow' | 'red' | 'blue' = 'green';
    let indicatorText = env.networkName;
    
    if (env.networkType === 'local') {
      indicatorColor = 'blue';
      indicatorText = `🔧 ${env.networkName}`;
      warnings.push('You are connected to a local development network');
    } else if (env.networkType === 'testnet') {
      indicatorColor = 'yellow';
      indicatorText = `🧪 ${env.networkName}`;
      if (env.chainId !== '1322837') {
        warnings.push('You are not connected to Swarm devnet');
      }
    } else if (env.networkType === 'mainnet') {
      indicatorColor = 'red';
      indicatorText = `⚠️ ${env.networkName}`;
      warnings.push('You are connected to mainnet - be careful with transactions');
    } else {
      indicatorColor = 'red';
      indicatorText = `❓ ${env.networkName}`;
      warnings.push('Unknown network detected');
    }
    
    if (env.isDevelopment && env.networkType !== 'local' && env.chainId !== '1322837') {
      warnings.push('Development mode detected but not using recommended networks');
    }
    
    return {
      showIndicator: env.isDevelopment || env.debugMode || warnings.length > 0,
      indicatorText,
      indicatorColor,
      warnings
    };
  }
}

/**
 * Transaction debugger for development mode
 */
export class TransactionDebugger {
  private provider: ethers.Provider;
  private developmentMode: boolean;

  constructor(provider: ethers.Provider) {
    this.provider = provider;
    this.developmentMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Get detailed transaction debug information
   */
  async getTransactionDebugInfo(txHash: string): Promise<TransactionDebugInfo> {
    const debugInfo: TransactionDebugInfo = {
      transactionHash: txHash,
      status: 'pending'
    };

    try {
      // Get transaction receipt
      const receipt = await this.provider.getTransactionReceipt(txHash);
      
      if (!receipt) {
        debugInfo.status = 'pending';
        return debugInfo;
      }

      debugInfo.blockNumber = receipt.blockNumber;
      debugInfo.gasUsed = receipt.gasUsed.toString();
      debugInfo.status = receipt.status === 1 ? 'success' : 'failed';
      debugInfo.logs = [...receipt.logs];

      // Get transaction details
      const tx = await this.provider.getTransaction(txHash);
      if (tx) {
        debugInfo.gasLimit = tx.gasLimit.toString();
        debugInfo.gasPrice = tx.gasPrice?.toString();
      }

      // Get block timestamp
      if (receipt.blockNumber) {
        const block = await this.provider.getBlock(receipt.blockNumber);
        if (block) {
          debugInfo.timestamp = new Date(block.timestamp * 1000).toISOString();
        }
      }

      // Try to get revert reason if transaction failed
      if (debugInfo.status === 'failed' && this.developmentMode) {
        try {
          debugInfo.revertReason = await this.getRevertReason(txHash);
        } catch (error) {
          console.warn('Could not get revert reason:', error);
        }
      }

      return debugInfo;
    } catch (error) {
      console.error('Error getting transaction debug info:', error);
      debugInfo.status = 'failed';
      return debugInfo;
    }
  }

  /**
   * Get revert reason for failed transaction (development mode only)
   */
  private async getRevertReason(txHash: string): Promise<string | undefined> {
    if (!this.developmentMode) return undefined;

    try {
      const tx = await this.provider.getTransaction(txHash);
      if (!tx) return undefined;

      // Try to replay the transaction to get the revert reason
      const result = await this.provider.call({
        to: tx.to,
        data: tx.data,
        value: tx.value,
        gasLimit: tx.gasLimit,
        gasPrice: tx.gasPrice,
        blockTag: tx.blockNumber ? tx.blockNumber - 1 : 'latest'
      });

      return result;
    } catch (error: any) {
      // Extract revert reason from error message
      if (error.reason) return error.reason;
      if (error.message) {
        const match = error.message.match(/revert (.+)/);
        if (match) return match[1];
      }
      return 'Unknown revert reason';
    }
  }

  /**
   * Generate debugging suggestions for failed transactions
   */
  generateDebuggingSuggestions(debugInfo: TransactionDebugInfo): string[] {
    const suggestions: string[] = [];

    if (debugInfo.status === 'failed') {
      suggestions.push('Transaction failed - check the error details below');
      
      if (debugInfo.revertReason) {
        suggestions.push(`Revert reason: ${debugInfo.revertReason}`);
      }
      
      suggestions.push('Common causes:');
      suggestions.push('• Insufficient balance or allowance');
      suggestions.push('• Gas limit too low');
      suggestions.push('• Contract function requirements not met');
      suggestions.push('• Network congestion or RPC issues');
    }

    if (debugInfo.status === 'pending') {
      suggestions.push('Transaction is still pending');
      suggestions.push('• Check network congestion');
      suggestions.push('• Verify gas price is sufficient');
      suggestions.push('• Wait for confirmation or try increasing gas');
    }

    if (this.developmentMode) {
      suggestions.push('Development mode debugging:');
      suggestions.push('• Check browser console for detailed errors');
      suggestions.push('• Verify contract is deployed correctly');
      suggestions.push('• Test with smaller amounts first');
    }

    return suggestions;
  }
}

/**
 * Development mode error handler with enhanced debugging
 */
export class DevelopmentModeErrorHandler {
  private static instance: DevelopmentModeErrorHandler;
  private developmentMode: boolean;
  private debugMode: boolean;

  constructor() {
    this.developmentMode = process.env.NODE_ENV === 'development';
    this.debugMode = this.developmentMode || process.env.REACT_APP_DEBUG === 'true';
  }

  static getInstance(): DevelopmentModeErrorHandler {
    if (!DevelopmentModeErrorHandler.instance) {
      DevelopmentModeErrorHandler.instance = new DevelopmentModeErrorHandler();
    }
    return DevelopmentModeErrorHandler.instance;
  }

  /**
   * Handle errors with development mode enhancements
   */
  handleError(error: unknown, context: string): DevelopmentError {
    const baseError = error instanceof Error ? error : new Error(String(error));
    
    const developmentError: DevelopmentError = {
      name: 'DevelopmentError',
      message: baseError.message,
      code: this.extractErrorCode(error),
      context,
      stack: baseError.stack
    };

    if (this.developmentMode) {
      developmentError.debugInfo = {
        originalError: error,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        context
      };

      developmentError.suggestions = this.generateSuggestions(error, context);
    }

    // Log detailed error information in development mode
    if (this.debugMode) {
      console.group(`🐛 Development Error: ${context}`);
      console.error('Error:', error);
      console.log('Context:', context);
      console.log('Suggestions:', developmentError.suggestions);
      console.groupEnd();
    }

    return developmentError;
  }

  /**
   * Extract error code from various error types
   */
  private extractErrorCode(error: unknown): string {
    if (error && typeof error === 'object') {
      if ('code' in error) return String((error as any).code);
      if ('name' in error) return String((error as any).name);
    }
    
    if (error instanceof Error) {
      return error.constructor.name;
    }
    
    return 'UNKNOWN_ERROR';
  }

  /**
   * Generate context-specific debugging suggestions
   */
  private generateSuggestions(error: unknown, context: string): string[] {
    const suggestions: string[] = [];
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

    // Context-specific suggestions
    if (context.includes('contract')) {
      suggestions.push('Contract-related debugging:');
      suggestions.push('• Verify contract is deployed to current network');
      suggestions.push('• Check contract address in configuration');
      suggestions.push('• Ensure ABI matches deployed contract');
    }

    if (context.includes('network')) {
      suggestions.push('Network-related debugging:');
      suggestions.push('• Check internet connection');
      suggestions.push('• Verify RPC endpoint is working');
      suggestions.push('• Try switching networks and back');
    }

    if (context.includes('transaction')) {
      suggestions.push('Transaction debugging:');
      suggestions.push('• Check account has sufficient balance');
      suggestions.push('• Verify gas settings are appropriate');
      suggestions.push('• Ensure MetaMask is unlocked');
    }

    // Error message-specific suggestions
    if (errorMessage.includes('user rejected')) {
      suggestions.push('User rejected the request - this is normal if intentional');
    }

    if (errorMessage.includes('insufficient funds')) {
      suggestions.push('Add more ETH or tokens to your wallet');
    }

    if (errorMessage.includes('network')) {
      suggestions.push('Check network connection and RPC endpoints');
    }

    if (errorMessage.includes('revert')) {
      suggestions.push('Contract execution failed - check function parameters');
    }

    // General development suggestions
    if (this.developmentMode) {
      suggestions.push('General debugging:');
      suggestions.push('• Check browser console for additional errors');
      suggestions.push('• Try refreshing the page');
      suggestions.push('• Clear browser cache if issues persist');
    }

    return suggestions;
  }

  /**
   * Check if error should show detailed debugging info
   */
  shouldShowDebugInfo(): boolean {
    return this.debugMode;
  }

  /**
   * Format error for display in development mode
   */
  formatErrorForDisplay(error: DevelopmentError): {
    title: string;
    message: string;
    details?: string;
    suggestions: string[];
  } {
    return {
      title: `Development Error: ${error.code}`,
      message: error.message,
      details: this.debugMode ? JSON.stringify(error.debugInfo, null, 2) : undefined,
      suggestions: error.suggestions || []
    };
  }
}